import React, { useState, useEffect, forwardRef } from 'react';
import {
  Checkbox,
  CustomPropertiesForm,
  FormTabs,
  Input,
  SelectOwner,
  InputStatus,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import { getBatchEditValue, Asset } from '../helpers';
import { useCustomProperties, useGlobalState, useMessage } from '../hooks';

/**
 * Form for adding and editing a Glossary.
 *
 * Selecting more than one glossary switches the form to batch edit, where each editable property is
 * gated behind an "Edit ..." checkbox and only the ticked ones are written; everything else keeps
 * each glossary's own value. Type is the one free text property offered there, so where the selected
 * glossaries disagree it holds the `'multiple'` sentinel from `getBatchEditValue` — see `onSubmit`
 * for how that is kept out of the payload.
 *
 * @param {object} props
 * @param {'add'|'edit'} props.mode
 * @param {object[]} props.data - Selected glossaries; empty when adding.
 * @param {Function} props.submitForm - Receives the payload; owned by the calling view.
 * @param {boolean} props.loading
 * @param {Function} props.setLoading
 * @param {Record<string, string>} [props.formErrors] - Field-keyed errors from a 400 response.
 * @param {React.Ref} ref - Forwarded to the `<form>` element so the side dialog can submit it.
 */
const _AddGlossaryForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    glossary_id: null,
    glossary_name: '',
    glossary_type: '',
    glossary_description: textareaInitialState({}),
    owner_principal_id: '',
    status_id: '',
  };

  const initialBatchEditItems = {
    batch_glossary_type: false,
    batch_owner_principal_id: false,
    batch_status_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [displayNames] = useGlobalState('displayNames');
  const [statuses] = useGlobalState('statuses');
  const { hideMessage } = useMessage();
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({
    objectName: Asset.Glossary,
    objectIds: data.map(x => x.glossary_id),
  });

  useEffect(() => {
    // Get form data
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        glossary_id: data[0].glossary_id,
        glossary_name: data[0].glossary_name || '',
        glossary_type: data[0].glossary_type || '',
        glossary_description: textareaInitialState({
          value: data[0].glossary_description,
        }),
        owner_principal_id: data[0].owner_principal_id,
        status_id: data[0].status_id,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        glossary_type: getBatchEditValue(data, 'glossary_type'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
        status_id: getBatchEditValue(data, 'status_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_glossary_status_id,
        owner_principal_id: loggedInUser?.principal_id,
      });
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser]);

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems(prev => ({ ...prev, [name]: value }));
        break;
      default:
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  /**
   * Builds the payload and hands it to the calling view's submitForm.
   *
   * In batch edit the payload is one row per selected glossary, carrying that glossary's own values
   * with the ticked properties overwritten. Otherwise it is the single form state, with the rich text
   * description converted back to its stored raw form.
   *
   * @param {React.FormEvent} event
   */
  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      // The type is a free text field, so it holds the 'multiple' sentinel while the selected
      // glossaries disagree and the field has not been typed in. Submitting that would write
      // "multiple" as the type, so an untouched field is read as clearing the value instead.
      if (tmp.glossary_type === 'multiple') {
        tmp.glossary_type = null;
      }

      dataToSubmit = data.map(item => {
        return {
          glossary_id: item.glossary_id,
          glossary_name: item.glossary_name,
          glossary_type: item.glossary_type,
          owner_principal_id: item.owner_principal_id,
          status_id: item.status_id,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.glossary_id),
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        glossary_description: textareaToRaw({
          value: formData.glossary_description,
        }),
        ...getCustomPropertyValuesForSubmit(),
      };
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData(() => {
      return {
        ...initialFormState,
        status_id: statuses.default_glossary_status_id,
        owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
      };
    });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="glossary_name"
            value={formData.glossary_name}
            statusValue={formData.status_id}
            assetCategory="glossary"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.glossary_name
                ? { valid: false, message: formErrors.glossary_name }
                : undefined
            }
          />
        </div>

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_glossary_type}
                    name="batch_glossary_type"
                    label="Edit Type"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Input
                type="text"
                name="glossary_type"
                // 'multiple' is the sentinel for "the selected glossaries have different types",
                // so it is shown as a placeholder rather than as text that would be submitted.
                // Batch edit reads the value straight off the row, where an unset type is null.
                value={formData.glossary_type === 'multiple' ? '' : formData.glossary_type || ''}
                placeholder={formData.glossary_type === 'multiple' ? '< Multiple >' : ''}
                label={!batchEdit}
                onChange={handleInputChange}
                title={displayNames?.glossary_type?.display_name_description}
                disabled={loading || (batchEdit && !batchEditItems.batch_glossary_type)}
                validation={
                  formErrors.glossary_type
                    ? { valid: false, message: formErrors.glossary_type }
                    : undefined
                }
                isOptional={true}
              />
            </div>

            {!batchEdit && (
              <div className="form-block vertical">
                <Textarea
                  name="glossary_description"
                  value={formData.glossary_description}
                  onChange={handleInputChange}
                  disabled={loading}
                  isOptional={true}
                />
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_owner_principal_id}
                    name="batch_owner_principal_id"
                    label="Edit Owner"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <SelectOwner
                name="owner_principal_id"
                value={
                  formData.owner_principal_id === 'multiple'
                    ? undefined
                    : formData.owner_principal_id
                }
                label={!batchEdit}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
              />
            </div>
          </div>

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddGlossaryForm = forwardRef(_AddGlossaryForm);
